/**
 * editorialNotebook.js
 *
 * Single source of truth for the BuildPilot Editorial Notebook page at
 * /editorial-notebook. The notebook is a reverse-chronological digest of
 * BuildPilot's most recent editorial opinions (the "BuildPilot opinion"
 * callouts that appear across the site).
 *
 * Why this exists:
 *   Today our editorial voice is scattered across /sources, the lifecycle
 *   pillars and supporting guides. The notebook gives the whole editorial
 *   stream one URL, which:
 *     1. Lets returning readers see what is new without revisiting every
 *        page.
 *     2. Becomes a strong topical-authority signal for SEO (this is the
 *        "BuildPilot opinion" entity for Google Knowledge Graph).
 *     3. Is shareable as a single link (newsletter, social, sales decks).
 *
 * Legal posture:
 *   Every entry is framed as "BuildPilot opinion" / editorial reading,
 *   never as advice. Real one-to-one advice lives behind One on One
 *   Support and is linked from the page footer.
 *
 * Curation rule:
 *   Only include opinions that are written by George Giannakakis (or
 *   reviewed by George before publication). George personally holds the
 *   credentials (B.Arch.Studies, M.Arch UniSA, RLA300580) that make these
 *   opinions accountable. The notebook is George's editorial voice; the
 *   organisation only publishes it.
 */

// Quarterly cadence. Add the latest entries to the TOP of the array.
// Schema-wise, the order in this array IS the display order.
export const NOTEBOOK_ENTRIES = [
  // Q1 2026
  {
    id: 'lifecycle-dream-2026q1',
    title: 'Stay in the dreaming stage longer than you think',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Lifecycle: Start Dreaming',
    excerpt:
      'In our reading, the dreaming stage is the most underused, highest-leverage stage of the whole journey, and it costs you nothing. Every $5,000 to $30,000 design variation we see after deposit traces back to a buyer who picked a floor plan in a display home on the day. Browse widely, save 8 to 12 favourites, then sleep on it.',
    sourceUrl: '/lifecycle/start-dreaming',
    sourceLabel: 'Start Dreaming pillar',
  },
  {
    id: 'lifecycle-plan-2026q1',
    title: 'The per-square-metre rate is the most misleading number in Adelaide building',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Lifecycle: Begin Planning',
    excerpt:
      'In our reading, the per-square-metre rate is the most misleading number in Adelaide building. We have seen $1,950/m² builds finish at $2,500/m² once site costs, provisional sum reconciliations and variations are added; and $3,200/m² custom builds come in at the quoted figure because the inclusions list was honest. Compare total project cost on identical inclusions, not the headline rate.',
    sourceUrl: '/lifecycle/begin-planning',
    sourceLabel: 'Begin Planning pillar',
  },
  {
    id: 'lifecycle-choose-2026q1',
    title: 'Match the builder to the brief, not the brief to the builder',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Lifecycle: Make Choices',
    excerpt:
      'In our reading, the biggest mistake at the Make Choices stage is picking a builder whose portfolio does not match what you actually want. A volume builder doing a heavily customised brief produces the worst-of-both-worlds outcome: project-home finishes at custom-home prices. Match the builder to the brief, not the brief to the builder.',
    sourceUrl: '/lifecycle/make-choices',
    sourceLabel: 'Make Choices pillar',
  },
  {
    id: 'lifecycle-build-2026q1',
    title: 'Frame-stage and pre-handover inspections are the cheapest insurance on any Adelaide build',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Lifecycle: Get Building',
    excerpt:
      'In our reading, the two cheapest pieces of insurance on any Adelaide build are the $600 to $900 frame-stage inspection and the $600 to $900 pre-handover inspection. We see them catch $20,000 to $80,000 worth of remediation that would otherwise be invisible until after handover, when leverage is gone.',
    sourceUrl: '/lifecycle/get-building',
    sourceLabel: 'Get Building pillar',
  },
  {
    id: 'lifecycle-own-2026q1',
    title: 'Most Adelaide owners under-use the 90-day defect liability window',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Lifecycle: Love Living',
    excerpt:
      'In our reading, most Adelaide owners under-use the 90-day defect liability window because they are too polite to compile a long list, or too busy moving in. The window does not extend, and the moment it closes your bargaining position drops sharply. Treat it like a checklist, not a confrontation.',
    sourceUrl: '/lifecycle/love-living',
    sourceLabel: 'Love Living pillar',
  },
  {
    id: 'fhb-deposit-2026q1',
    title: 'The Regional First Home Buyer Guarantee is the most underused option we see',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'First Home Buyers',
    excerpt:
      'Our reading: the $125K single / $200K couple income caps rule out a meaningful slice of dual-income Adelaide buyers who would otherwise qualify on deposit alone. The Regional First Home Buyer Guarantee is the most underused option we see; it covers Mount Barker, Murray Bridge and Strathalbyn with higher property caps than the metro scheme.',
    sourceUrl: '/sources',
    sourceLabel: 'Source Index, Housing Australia',
  },
  {
    id: 'ncc-7star-2026q1',
    title: 'The 7-star NatHERS minimum is still surprising Adelaide buyers',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Energy & Compliance',
    excerpt:
      'The 7-star NatHERS minimum that came in May 2024 added an estimated $5,000 to $15,000 to a typical Adelaide build, mostly in glazing, insulation and orientation work. In our reading the up-front sticker shock surprises buyers because most builders had not refreshed their base inclusions to absorb it through 2024 to 2025.',
    sourceUrl: '/sources',
    sourceLabel: 'Source Index, ABCB',
  },
  {
    id: 'homestart-shared-equity-2026q1',
    title: 'HomeStart Shared Equity is materially better than the federal 5% Deposit Scheme for under-$115K households',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'First Home Buyers',
    excerpt:
      'HomeStart is underused by Adelaide buyers because the products are SA-specific and nationwide comparison sites do not surface them well. In our reading the Shared Equity Option is materially better than the federal 5% Deposit Scheme for buyers under $115,000 household income, but it cuts off above that threshold.',
    sourceUrl: '/sources',
    sourceLabel: 'Source Index, HomeStart',
  },
  {
    id: 'contracts-bwca-2026q1',
    title: 'The single most useful piece of SA legislation for Adelaide buyers to skim before signing',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Building Contracts',
    excerpt:
      'The Building Work Contractors Act 1995 (SA) is the single most useful piece of legislation for Adelaide buyers to skim before signing a contract. In our experience disputes most often start in two places, the 5% deposit cap (Section 11) and the progress payment provisions, both worth a literal read.',
    sourceUrl: '/sources',
    sourceLabel: 'Source Index, BWC Act',
  },
  {
    id: 'hia-contract-2026q1',
    title: 'Two HIA standard contract clauses cause most of the contract surprises we see',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Building Contracts',
    excerpt:
      'The HIA standard contract is a starting point, not a finishing point. In our reading two clauses cause most contract surprises, the variations clause and the provisional sum reconciliation clause, both worth getting independent eyes on before signing.',
    sourceUrl: '/sources',
    sourceLabel: 'Source Index, HIA',
  },
  {
    id: 'livable-housing-2026q1',
    title: 'Most Adelaide builders are not yet pricing the 2025 Livable Housing rules into the base spec',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Energy & Compliance',
    excerpt:
      'The biggest 2025 change for Adelaide buyers is the Livable Housing accessibility requirements (wider doorways, reinforced bathroom walls, step-free entry). In our reading most builders are not yet pricing these into the base spec; expect a $4,000 to $8,000 code compliance variation if your contract was drafted before NCC 2025 adoption.',
    sourceUrl: '/sources',
    sourceLabel: 'Source Index, NCC 2025',
  },
  {
    id: 'bal-rating-2026q1',
    title: 'BAL-29 is where Adelaide Hills cost and design constraints really start to bite',
    date: '2026-03-01',
    quarter: 'Q1 2026',
    topic: 'Bushfire & Land',
    excerpt:
      'Our reading: BAL-12.5 adds modest cost (around $5,000 to $10,000) and rarely changes the design. BAL-29 is where both cost and design constraints spike, over $20,000 extra plus shutter and ember-screen requirements. BAL-40 and BAL-FZ are where most Adelaide Hills buyers we work with reconsider whether a particular block is the right one.',
    sourceUrl: '/sources',
    sourceLabel: 'Source Index, Bushfire',
  },
];

export const getNotebookByQuarter = () => {
  const grouped = {};
  for (const entry of NOTEBOOK_ENTRIES) {
    if (!grouped[entry.quarter]) grouped[entry.quarter] = [];
    grouped[entry.quarter].push(entry);
  }
  return grouped;
};

export const getNotebookTotals = () => {
  const quarters = new Set(NOTEBOOK_ENTRIES.map((e) => e.quarter));
  const topics = new Set(NOTEBOOK_ENTRIES.map((e) => e.topic));
  return {
    totalEntries: NOTEBOOK_ENTRIES.length,
    totalQuarters: quarters.size,
    totalTopics: topics.size,
    latestDate: NOTEBOOK_ENTRIES[0]?.date || '2026-03-01',
  };
};
